import { makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { actions, store, useTypedDispatch, useTypedSelector } from '../../../../redux';
import { theme } from '../../../../theme/theme';
import { Asset, AssetOverview } from '../../../../types/asset';
import { Plan } from '../../../../types/plan';
import { Property } from '../../../global/Property';
import { SearchBar } from '../../../global/SearchBar';
import { SectionState } from '../InspectionCreationWizard';

const mod = 'sections.AssetData';

/**
 * Number of internal steps this section contains.
 */
export const steps = 3;
export const label = `${mod}.summary-label`;

/**
 * Header generator.
 *
 * @param t translation function
 * @param state the current section state
 * @param focused if is the current section
 * @param asset selected asset
 * @param plan asset plan
 * @returns header title and subtitle
 */
export const header = (t: TFunction<string[]>, state: SectionState, focused: boolean, asset?: Asset, plan?: Plan) => ({
    title: t(`${mod}.header-title${!focused ? '-small' : state.step === 0 ? '-search' : ''}`, { name: asset?.name }),
    subtitle: t(`${mod}.header-subtitle${!focused ? '-small' : state.step === 0 ? '-search' : ''}`, { count: 10 }),
});

/**
 * Asset search and details section of the inspection creation process.
 *
 * @param props.state the current section state
 * @param props.next function to advance to the next step
 */
export const Component = (props: { state: SectionState; next: () => void }) => {
    return props.state.step === 0 ? <AssetSearchBar {...props} /> : <AssetDetails {...props} />;
};

const useAssetSearchBarClasses = makeStyles({
    root: {
        flex: 1,
        marginBottom: 0,
    },
    option: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: '18px 36px',
        boxShadow: '0 -1px 0 0 rgba(0, 0, 0, 0.12)',
        '& :first-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.content.caption.paragraph,
        },
        '& :last-child': {
            color: theme.colors.darkBlue[5],
            ...theme.typography.content.label.medium,
        },
    },
});

/**
 * Search bar of the asset section.
 *
 * @param props.state the current section state
 * @param props.next function to advance to the next step
 */
const AssetSearchBar = (props: { state: SectionState; next: () => void }) => {
    const classes = useAssetSearchBarClasses();
    const { t } = useTranslation(['inspection-creation']);
    const dispatch = useTypedDispatch();
    const { asset_list } = useTypedSelector(state => ({ asset_list: state.asset_list }));

    return (
        <SearchBar
            className={classes.root}
            label={t(`${mod}.search-bar-label`)}
            loading={asset_list.fetching}
            options={asset_list.assets.flat().filter(a => !!a) as AssetOverview[]}
            renderLoading={Array(3)
                .fill(undefined)
                .map((_, i) => (
                    <div key={i} className={classes.option}>
                        <Skeleton animation='wave' width='15%' />
                        <Skeleton animation='wave' width='50%' />
                    </div>
                ))}
            renderEmpty={t(`${mod}.search-bar-empty`)}
            renderOption={option => (
                <div className={classes.option}>
                    <span>{option.code}</span>
                    <span>{option.name}</span>
                </div>
            )}
            renderLabel={option => `${option.code}: ${option.name}`}
            loadingIgnoreOptions
            filterOptions='no-filter'
            debounce={300}
            onSearch={query => dispatch(actions.asset_list.get({ query }))}
            onSelect={async value => {
                if (!value) return;
                try {
                    await dispatch(actions.asset.get(value.code, true));
                    const asset = store.getState().asset.asset;
                    await dispatch(
                        asset?.inspection_plan != undefined
                            ? actions.plan.get(asset.inspection_plan)
                            : actions.plan.create(),
                    );
                    props.next();
                } catch (error) {}
            }}
        />
    );
};

const useAssetDetailsSectionClasses = makeStyles({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
        margin: '-5px -10px',
        '& > *': {
            flex: '1 0 calc(100% / 3 - 20px)',
            minWidth: 200,
            margin: '5px 10px',
        },
    },
});

/**
 * List the metadata properties of an asset.
 *
 * @param props.state the current section state
 * @param props.next function to advance to the next step
 */
const AssetDetails = (props: { state: SectionState; next: () => void }) => {
    const classes = useAssetDetailsSectionClasses();
    const { t } = useTranslation(['inspection-creation']);
    const dispatch = useTypedDispatch();
    const { asset, version, asset_type } = useTypedSelector(state => ({
        asset: state.asset.changed,
        version: state.asset.changes.length,
        asset_type: state.asset_type,
    }));
    const metadata = Object.entries(asset?.metadata ?? {}).filter(([name]) => !name.endsWith('design'));
    const needsAttention = t(`${mod}.needs-attention`);

    React.useEffect(() => void dispatch(actions.asset_type.get()), []);

    if (!asset) return <span>{'#asset not available in store'}</span>;

    return (
        <div className={classes.root}>
            <Property name={'code'} value={asset.code} />
            <Property
                key={`name-${version}`}
                name='name'
                value={asset.name}
                error={asset.name == undefined || asset.name.length === 0 ? needsAttention : undefined}
                editable={props.state.editable}
                onChange={(name, value) => dispatch(actions.asset.edit({ [name]: value }))}
            />
            <Property
                key={`type-${version}-${asset_type.fetching}`}
                name='type'
                value={asset.type}
                options={Object.values(asset_type.items)}
                optionEquals={(a, b) => a?.id === b?.id}
                renderOption={assetType => assetType.name}
                unselect={'None'}
                error={!asset.type ? needsAttention : undefined}
                editable={props.state.editable}
                onChange={(name, value) => dispatch(actions.asset.edit({ [name]: value }))}
            />
            {props.state.step === props.state.steps - 1 &&
                metadata.map(([name, value]) => (
                    <Property
                        key={`metadata-${name}-${version}`}
                        name={name}
                        value={value}
                        error={value == undefined || value.length === 0 ? needsAttention : undefined}
                        snake
                        editable={props.state.editable}
                        onChange={(name, value) => dispatch(actions.asset.edit({ metadata: { [name]: value } }))}
                    />
                ))}
            {[...Array(3 - (metadata.length % 3))].map((_, i) => (
                <div key={i} />
            ))}
        </div>
    );
};
